(() => {
  "use strict";

  const SELECTORS = {
    navItem: "[data-pv-nav-item]",
    menuTrigger: "[data-pv-menu-trigger]",
    mega: "[data-pv-mega]",
    backdrop: "[data-pv-backdrop]",
    drawer: "[data-pv-drawer]",
    drawerOpen: "[data-pv-drawer-open]",
    drawerClose: "[data-pv-drawer-close]",
    accordionTrigger: "[data-pv-accordion-trigger]",
    accordionPanel: "[data-pv-accordion-panel]",
    roomTrigger: "[data-pv-room-trigger]",
    roomPanel: "[data-pv-room-panel]",
    search: "[data-pv-search]",
    searchOpen: "[data-pv-search-open]",
    searchClose: "[data-pv-search-close]",
    searchInput: "[data-pv-search-input]",
    regionMovable: "[data-pv-region-movable]",
    regionDesktop: "[data-pv-region-desktop]",
    regionMobile: "[data-pv-region-mobile]",
  };


  /* Never collapse the panel past the point where the input itself would be
     unusable, even if a browser reports an implausibly small visual viewport. */
  const SEARCH_MIN_HEIGHT = 180;

  const FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  class PovelleaHeader extends HTMLElement {
    constructor() {
      super();

      this.openDelay = 140;
      this.switchDelay = 160;
      this.hoverSettleDelay = 110;
      this.closeDelay = 360;
      this.activeMenuItem = null;
      this.pendingMenuItem = null;
      this.lastFocusedElement = null;
      this.stickyHost = null;
      this.lastScrollY = window.scrollY;
      this.scrollTicking = false;
      this.openTimer = null;
      this.closeTimer = null;
      this.lastPointerMoveAt = 0;
      this.isDesktopHover = window.matchMedia("(min-width: 1100px) and (hover: hover) and (pointer: fine)");

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentEscape = this.onDocumentEscape.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.syncSearchViewport = this.syncSearchViewport.bind(this);
    }

    connectedCallback() {
      if (this.dataset.pvInitialized === "true") return;
      this.dataset.pvInitialized = "true";

      this.backdrop = this.querySelector(SELECTORS.backdrop);
      this.drawer = this.querySelector(SELECTORS.drawer);
      this.drawerOpenButton = this.querySelector(SELECTORS.drawerOpen);
      this.search = this.querySelector(SELECTORS.search);
      this.searchOpenButton = this.querySelector(SELECTORS.searchOpen);
      this.regionMovable = this.querySelector(SELECTORS.regionMovable);
      this.regionDesktop = this.querySelector(SELECTORS.regionDesktop);
      this.regionMobile = this.querySelector(SELECTORS.regionMobile);
      this.stickyHost = this.closest(".shopify-section-header");

      if (this.stickyHost) {
        this.stickyHost.classList.add("pv-sticky-host");
        this.stickyHost.classList.toggle(
          "pv-sticky-host--disabled",
          (this.dataset.stickyType || "on-scroll-up") === "none"
        );
      }

      this.addEventListener("click", this.onClick);
      this.addEventListener("keydown", this.onKeydown);
      this.addEventListener("focusin", this.onDocumentFocusIn);
      document.addEventListener("keydown", this.onDocumentEscape);
      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize, { passive: true });

      if (this.isDesktopHover.addEventListener) {
        this.isDesktopHover.addEventListener("change", this.onMediaChange);
      } else {
        this.isDesktopHover.addListener(this.onMediaChange);
      }

      this.setupHoverMenus();
      this.setupBackdropHover();
      this.setupResizeObserver();
      this.syncRegionSlot();
      this.updateHeaderPosition();
      this.updateStickyState();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onClick);
      this.removeEventListener("keydown", this.onKeydown);
      this.removeEventListener("focusin", this.onDocumentFocusIn);
      document.removeEventListener("keydown", this.onDocumentEscape);
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);

      if (this.isDesktopHover.removeEventListener) {
        this.isDesktopHover.removeEventListener("change", this.onMediaChange);
      } else {
        this.isDesktopHover.removeListener(this.onMediaChange);
      }

      this.resizeObserver?.disconnect();
      this.unbindSearchViewport();
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      document.documentElement.classList.remove("pv-scroll-locked");
      this.stickyHost?.classList.remove(
        "pv-sticky-host",
        "pv-sticky-host--disabled",
        "pv-sticky-host--hidden"
      );
    }

    setupHoverMenus() {
      this.querySelectorAll(SELECTORS.navItem).forEach((item) => {
        const trigger = item.querySelector(SELECTORS.menuTrigger);
        const mega = item.querySelector(SELECTORS.mega);
        if (!trigger || !mega) return;

        trigger.addEventListener("pointermove", () => {
          this.lastPointerMoveAt = performance.now();
        });

        trigger.addEventListener("pointerenter", () => {
          if (!this.isDesktopHover.matches) return;
          window.clearTimeout(this.closeTimer);
          this.queueMegaOpen(item, trigger);
        });

        trigger.addEventListener("pointerleave", () => {
          if (!this.isDesktopHover.matches) return;
          if (this.pendingMenuItem === item) {
            window.clearTimeout(this.openTimer);
            this.pendingMenuItem = null;
          }
        });

        mega.addEventListener("pointerenter", () => {
          if (!this.isDesktopHover.matches || this.activeMenuItem !== item) return;
          window.clearTimeout(this.closeTimer);
        });

      });
    }

    queueMegaOpen(item, trigger) {
      /*
       * Moving from one header label to another moves the panel with it. The
       * switch is deliberate rather than instant: the pointer has to settle on
       * the new label for `switchDelay`, so sweeping the cursor across the row
       * to reach the cart or the search button never flickers four panels open
       * on the way past.
       */
      window.clearTimeout(this.openTimer);
      this.pendingMenuItem = item;
      this.lastPointerMoveAt = performance.now();

      const initialDelay =
        this.activeMenuItem && this.activeMenuItem !== item
          ? this.switchDelay
          : this.openDelay;

      const attemptOpen = () => {
        if (
          this.pendingMenuItem !== item ||
          !trigger.matches(":hover") ||
          !this.isDesktopHover.matches
        ) {
          return;
        }

        const idleFor = performance.now() - this.lastPointerMoveAt;
        if (idleFor < this.hoverSettleDelay) {
          this.openTimer = window.setTimeout(
            attemptOpen,
            this.hoverSettleDelay - idleFor
          );
          return;
        }

        this.openMega(item);
      };

      this.openTimer = window.setTimeout(attemptOpen, initialDelay);
    }

    setupBackdropHover() {
      if (!this.backdrop) return;

      this.backdrop.addEventListener("pointerenter", () => {
        if (!this.isDesktopHover.matches || !this.activeMenuItem) return;
        this.scheduleMegaClose(180);
      });

      this.backdrop.addEventListener("pointerleave", () => {
        window.clearTimeout(this.closeTimer);
      });
    }

    setupResizeObserver() {
      if (!("ResizeObserver" in window)) return;

      this.resizeObserver = new ResizeObserver(() => {
        this.updateHeaderPosition();
      });
      this.resizeObserver.observe(this);
    }

    onClick(event) {
      const menuTrigger = event.target.closest(SELECTORS.menuTrigger);
      if (menuTrigger && this.contains(menuTrigger)) {
        event.preventDefault();
        const item = menuTrigger.closest(SELECTORS.navItem);
        if (item === this.activeMenuItem) {
          this.closeMega({ restoreFocus: false });
        } else {
          this.openMega(item);
        }
        return;
      }

      const drawerOpen = event.target.closest(SELECTORS.drawerOpen);
      if (drawerOpen && this.contains(drawerOpen)) {
        event.preventDefault();
        this.openDrawer(drawerOpen);
        return;
      }

      const drawerClose = event.target.closest(SELECTORS.drawerClose);
      if (drawerClose && this.contains(drawerClose)) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const accordionTrigger = event.target.closest(SELECTORS.accordionTrigger);
      if (accordionTrigger && this.contains(accordionTrigger)) {
        event.preventDefault();
        this.toggleAccordion(accordionTrigger);
        return;
      }

      const roomTrigger = event.target.closest(SELECTORS.roomTrigger);
      if (roomTrigger && this.contains(roomTrigger)) {
        event.preventDefault();
        this.toggleRoomAccordion(roomTrigger);
        return;
      }

      const searchOpen = event.target.closest(SELECTORS.searchOpen);
      if (searchOpen && this.contains(searchOpen)) {
        event.preventDefault();
        this.openSearch(searchOpen);
        return;
      }

      const searchClose = event.target.closest(SELECTORS.searchClose);
      if (searchClose && this.contains(searchClose)) {
        event.preventDefault();
        this.closeSearch();
        return;
      }

      const backdrop = event.target.closest(SELECTORS.backdrop);
      if (backdrop && this.contains(backdrop)) {
        event.preventDefault();
        // The backdrop deliberately leaves the search open - the visible close
        // button is its approved control. Drawer and mega menu are unchanged.
        this.closeAll({ restoreFocus: true, includeSearch: false });
        return;
      }

      const megaLink = event.target.closest(`${SELECTORS.mega} a[href]`);
      if (megaLink && this.contains(megaLink)) {
        /*
         * Close only after this link received its click. Default navigation is
         * preserved, so the browser follows this exact href.
         */
        this.closeMega({ restoreFocus: false });
        return;
      }

      const drawerLink = event.target.closest(`${SELECTORS.drawer} a[href]`);
      if (drawerLink && this.contains(drawerLink)) {
        this.closeDrawer({ restoreFocus: false });
      }
    }

    /*
     * A panel opened by hover has not moved focus, so the header's own keydown
     * listener never sees the key. Escape has to reach it from wherever the
     * reader happens to be. Events that started inside the header are left to
     * the handler below, which also restores focus to the trigger.
     */
    onDocumentEscape(event) {
      if (event.key !== "Escape" || !this.activeMenuItem) return;
      if (this.contains(event.target)) return;

      this.closeMega({ restoreFocus: false });
    }

    onKeydown(event) {
      if (event.key === "Escape") {
        if (this.isDrawerOpen() || this.isSearchOpen() || this.activeMenuItem) {
          event.preventDefault();
          this.closeAll({ restoreFocus: true });
        }
        return;
      }

      if (event.key === "Tab") {
        const trap = this.isDrawerOpen()
          ? this.drawer
          : this.isSearchOpen()
            ? this.search
            : null;

        if (trap) this.trapFocus(event, trap);
      }

    }

    onDocumentFocusIn(event) {
      if (!this.activeMenuItem) return;

      const currentMega = this.activeMenuItem.querySelector(SELECTORS.mega);
      if (
        !this.activeMenuItem.contains(event.target) &&
        currentMega &&
        !currentMega.contains(event.target)
      ) {
        this.closeMega({ restoreFocus: false });
      }
    }

    onScroll() {
      if (this.scrollTicking) return;

      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        const currentY = Math.max(window.scrollY, 0);
        const delta = currentY - this.lastScrollY;

        if (Math.abs(delta) > 2) {
          if (this.activeMenuItem) this.closeMega({ restoreFocus: false });

          /*
           * The search panel deliberately does NOT close here any more.
           *
           * It used to, which was harmless while the panel was a short strip over
           * a scrollable page. Now that opening it locks the body, any scroll
           * event that arrives while it is open is spurious rather than a
           * deliberate page scroll, and every one of them was closing the panel
           * under the customer:
           *   - engaging the lock clamps a scrolled document, firing a large delta;
           *   - iOS rubber-banding and scroll chaining from the panel's own
           *     scroller reach the document;
           *   - the software keyboard shifts the viewport;
           *   - rotating re-clamps scrollY against the new document height.
           * The panel closes through the close button, Escape, a backdrop tap,
           * or navigation - all of which are explicit.
           */

          this.updateStickyState(currentY, delta);
          this.lastScrollY = currentY;
        }

        this.updateHeaderPosition();
        this.scrollTicking = false;
      });
    }

    onResize() {
      this.updateHeaderPosition();
      this.syncRegionSlot();

      if (window.innerWidth >= 1100 && this.isDrawerOpen()) {
        this.closeDrawer({ restoreFocus: false });
      }

      if (window.innerWidth < 1100 && this.activeMenuItem) {
        this.closeMega({ restoreFocus: false });
      }
    }

    onMediaChange() {
      this.closeMega({ restoreFocus: false });
    }

    updateStickyState(currentY = window.scrollY, delta = 0) {
      const stickyType = this.dataset.stickyType || "on-scroll-up";
      const threshold = this.offsetHeight + 24;
      const hasOpenLayer = this.activeMenuItem || this.isDrawerOpen() || this.isSearchOpen();

      this.classList.toggle("is-sticky", currentY > 2);

      if (stickyType === "none" || stickyType === "always" || hasOpenLayer) {
        this.setHiddenState(false);
        return;
      }

      if (currentY <= 8) {
        this.setHiddenState(false);
      } else if (delta > 0 && currentY > threshold) {
        this.setHiddenState(true);
      } else if (delta < 0) {
        this.setHiddenState(false);
      }
    }

    setHiddenState(isHidden) {
      this.classList.toggle("is-hidden", isHidden);
      this.stickyHost?.classList.toggle("pv-sticky-host--hidden", isHidden);
    }

    updateHeaderPosition() {
      const rect = this.getBoundingClientRect();
      const bottom = Math.max(rect.bottom, 0);
      this.style.setProperty("--pv-header-bottom", `${bottom}px`);
      document.documentElement.style.setProperty("--header-height", `${this.offsetHeight}px`);
    }

    openMega(item) {
      if (!item || window.innerWidth < 1100) return;

      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.pendingMenuItem = null;
      this.closeSearch({ restoreFocus: false });

      if (this.activeMenuItem && this.activeMenuItem !== item) {
        this.setMegaState(this.activeMenuItem, false);
      }

      this.activeMenuItem = item;
      this.setMegaState(item, true);
      this.classList.add("has-open-mega");
      this.setHiddenState(false);
      this.updateHeaderPosition();
      this.syncBackdrop();
    }

    scheduleMegaClose(delay = this.closeDelay) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.closeMega({ restoreFocus: false });
      }, delay);
    }

    closeMega({ restoreFocus = false } = {}) {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.pendingMenuItem = null;
      if (!this.activeMenuItem) {
        this.classList.remove("has-open-mega");
        return;
      }

      const trigger = this.activeMenuItem.querySelector(SELECTORS.menuTrigger);
      this.setMegaState(this.activeMenuItem, false);
      this.activeMenuItem = null;
      this.classList.remove("has-open-mega");
      this.syncBackdrop();

      if (restoreFocus && trigger) trigger.focus({ preventScroll: true });
    }

    setMegaState(item, isOpen) {
      const trigger = item.querySelector(SELECTORS.menuTrigger);
      const mega = item.querySelector(SELECTORS.mega);
      if (!trigger || !mega) return;

      /*
       * Closed desktop mega menus must be removed from hit testing entirely.
       * An active child panel deliberately uses pointer-events/visibility, so
       * relying on the parent animation alone can leave an invisible link
       * layer over the page.
       */
      if (isOpen) {
        mega.hidden = false;
        mega.removeAttribute("inert");
      } else {
        mega.setAttribute("inert", "");
      }

      trigger.setAttribute("aria-expanded", String(isOpen));
      mega.setAttribute("aria-hidden", String(!isOpen));
      mega.classList.toggle("is-open", isOpen);

      if (!isOpen) {
        mega.hidden = true;
      }
    }

    openDrawer(trigger) {
      if (!this.drawer) return;

      this.closeMega({ restoreFocus: false });
      this.closeSearch({ restoreFocus: false });
      this.lastFocusedElement = trigger || document.activeElement;
      this.drawer.classList.add("is-open");
      this.drawer.setAttribute("aria-hidden", "false");
      this.drawerOpenButton?.setAttribute("aria-expanded", "true");
      document.documentElement.classList.add("pv-scroll-locked");
      // Its own state, not pv-scroll-locked: the search overlay sets that flag
      // too, and only the menu needs the floating WhatsApp control to stand
      // down. Styled in povellea-header.css.
      document.documentElement.classList.add("pv-drawer-open");
      this.setHiddenState(false);
      this.syncBackdrop();

      window.requestAnimationFrame(() => {
        this.drawer.querySelector(SELECTORS.drawerClose)?.focus({ preventScroll: true });
      });
    }

    closeDrawer({ restoreFocus = true } = {}) {
      if (!this.drawer || !this.isDrawerOpen()) return;

      this.drawer.classList.remove("is-open");
      this.drawer.setAttribute("aria-hidden", "true");
      this.drawerOpenButton?.setAttribute("aria-expanded", "false");
      document.documentElement.classList.remove("pv-scroll-locked");
      document.documentElement.classList.remove("pv-drawer-open");
      this.closeAllAccordions();
      this.closeAllRoomAccordions();
      this.syncBackdrop();

      if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    isDrawerOpen() {
      return Boolean(this.drawer?.classList.contains("is-open"));
    }

    toggleAccordion(trigger) {
      const panelId = trigger.getAttribute("aria-controls");
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (!panel) return;

      const willOpen = trigger.getAttribute("aria-expanded") !== "true";
      this.closeAllAccordions(trigger);
      this.closeAllRoomAccordions(null, panel);

      trigger.setAttribute("aria-expanded", String(willOpen));
      panel.classList.toggle("is-open", willOpen);
      panel.setAttribute("aria-hidden", String(!willOpen));
    }

    closeAllAccordions(exceptTrigger = null) {
      this.querySelectorAll(SELECTORS.accordionTrigger).forEach((trigger) => {
        if (trigger === exceptTrigger) return;

        const panelId = trigger.getAttribute("aria-controls");
        const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
        trigger.setAttribute("aria-expanded", "false");
        panel?.classList.remove("is-open");
        panel?.setAttribute("aria-hidden", "true");
        if (panel) this.closeAllRoomAccordions(null, panel);
      });
    }

    toggleRoomAccordion(trigger) {
      const panelId = trigger.getAttribute("aria-controls");
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (!panel) return;

      const parentPanel = trigger.closest(SELECTORS.accordionPanel);
      const willOpen = trigger.getAttribute("aria-expanded") !== "true";
      this.closeAllRoomAccordions(trigger, parentPanel);

      trigger.setAttribute("aria-expanded", String(willOpen));
      panel.classList.toggle("is-open", willOpen);
      panel.setAttribute("aria-hidden", String(!willOpen));
    }

    closeAllRoomAccordions(exceptTrigger = null, scope = this) {
      scope.querySelectorAll(SELECTORS.roomTrigger).forEach((trigger) => {
        if (trigger === exceptTrigger) return;

        const panelId = trigger.getAttribute("aria-controls");
        const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
        trigger.setAttribute("aria-expanded", "false");
        panel?.classList.remove("is-open");
        panel?.setAttribute("aria-hidden", "true");
      });
    }

    openSearch(trigger) {
      if (!this.search) return;

      this.closeMega({ restoreFocus: false });
      this.closeDrawer({ restoreFocus: false });
      this.lastFocusedElement = trigger || document.activeElement;
      this.search.classList.add("is-open");
      this.search.setAttribute("aria-hidden", "false");
      this.searchOpenButton?.setAttribute("aria-expanded", "true");
      // The comments in this file (and the scroll handler's reasoning for not
      // closing the panel on scroll) assume the open search locks the page.
      // The lock itself had gone missing, so the page scrolled freely behind
      // the panel. Same flag as the menu drawer; the shared Lenis pause in
      // theme.liquid keys off it too.
      document.documentElement.classList.add("pv-scroll-locked");
      this.setHiddenState(false);
      this.syncBackdrop();

      this.bindSearchViewport();

      window.requestAnimationFrame(() => {
        this.search.querySelector(SELECTORS.searchInput)?.focus({ preventScroll: true });
      });
    }

    /* iOS widely ignores overflow:hidden on the body, so the page kept scrolling
       behind the open panel and the panel's own scroller chained into it. Pinning
       the body is what actually holds it, but pinning alone snaps the document to
       the top, so the body is offset by the scroll position it had and that exact
       position is restored on close. */
    /* The software keyboard changes the visual viewport without changing the
       layout viewport on iOS, so neither dvh nor a max-height media query reacts
       to it. visualViewport is the only reliable signal. Where it is missing the
       stylesheet's own dvh fallback stays in force and nothing here runs. */
    bindSearchViewport() {
      const viewport = window.visualViewport;
      if (!viewport) return;

      this.syncSearchViewport();
      viewport.addEventListener("resize", this.syncSearchViewport);
      viewport.addEventListener("scroll", this.syncSearchViewport);
    }

    unbindSearchViewport() {
      const viewport = window.visualViewport;

      viewport?.removeEventListener("resize", this.syncSearchViewport);
      viewport?.removeEventListener("scroll", this.syncSearchViewport);

      this.search?.style.removeProperty("--pv-search-available");
    }

    syncSearchViewport() {
      const viewport = window.visualViewport;
      if (!this.search || !viewport) return;

      // Read the used value of `top` rather than getBoundingClientRect().top: the
      // panel is translated on the Y axis while it animates open, and measuring
      // mid-transition made the panel 8px taller than the space beneath it.
      // Computed `top` is the untransformed offset. offsetTop is the visual
      // viewport's own offset inside the layout viewport, so subtracting it gives
      // how far the panel sits from the top of what the customer can see.
      const panelTop = parseFloat(getComputedStyle(this.search).top);
      const topWithinVisual =
        (Number.isFinite(panelTop) ? panelTop : this.search.getBoundingClientRect().top) -
        viewport.offsetTop;
      const available = Math.max(
        SEARCH_MIN_HEIGHT,
        Math.round(viewport.height - topWithinVisual)
      );

      this.search.style.setProperty("--pv-search-available", `${available}px`);
    }

    closeSearch({ restoreFocus = true } = {}) {
      if (!this.search || !this.isSearchOpen()) return;

      this.search.classList.remove("is-open");
      this.search.setAttribute("aria-hidden", "true");
      this.searchOpenButton?.setAttribute("aria-expanded", "false");
      // Only lift the page lock when the menu drawer isn't holding it: the
      // drawer's open path closes the search first and re-adds the class, but
      // ordering shouldn't be load-bearing.
      if (!this.isDrawerOpen()) {
        document.documentElement.classList.remove("pv-scroll-locked");
      }
      this.syncBackdrop();

      this.unbindSearchViewport();

      if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    isSearchOpen() {
      return Boolean(this.search?.classList.contains("is-open"));
    }

    closeAll({ restoreFocus = true, includeSearch = true } = {}) {
      if (this.isDrawerOpen()) {
        this.closeDrawer({ restoreFocus });
        return;
      }

      if (this.isSearchOpen()) {
        // A caller that opts out leaves the search alone rather than falling
        // through to the mega menu below.
        if (!includeSearch) return;
        this.closeSearch({ restoreFocus });
        return;
      }

      this.closeMega({ restoreFocus });
    }

    syncBackdrop() {
      if (!this.backdrop) return;

      const shouldShow = Boolean(this.activeMenuItem || this.isDrawerOpen() || this.isSearchOpen());

      if (shouldShow) {
        this.backdrop.hidden = false;
        window.requestAnimationFrame(() => this.backdrop.classList.add("is-visible"));
      } else {
        this.backdrop.classList.remove("is-visible");
        window.setTimeout(() => {
          if (!this.activeMenuItem && !this.isDrawerOpen() && !this.isSearchOpen()) {
            this.backdrop.hidden = true;
          }
        }, 190);
      }
    }

    syncRegionSlot() {
      if (!this.regionMovable || !this.regionDesktop || !this.regionMobile) return;

      const destination = window.innerWidth < 1100 ? this.regionMobile : this.regionDesktop;
      if (this.regionMovable.parentElement !== destination) {
        destination.appendChild(this.regionMovable);
      }
    }

    trapFocus(event, container) {
      const focusable = [...container.querySelectorAll(FOCUSABLE)].filter((element) => {
        return !element.hasAttribute("hidden") && element.getClientRects().length > 0;
      });

      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get("povellea-header")) {
    customElements.define("povellea-header", PovelleaHeader);
  }
})();
